import * as Dialog from "@radix-ui/react-dialog";
import { Archive, Check, ChevronRight, FileText, Pencil, Sparkle, TriangleAlert } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import { useEffect, useLayoutEffect, useRef, useState, type MouseEvent } from "react";
import { Link } from "react-router";
import { useAkten, useBestaetigen, useDokument, useGezeigt, usePersonen } from "../api/abfragen";
import { vorschlagGeaendert } from "../api/client";
import { useNamen } from "../api/namen";
import type { Bestaetigung, Dokument, DokumentDetail, Vorschlag } from "../api/typen";
import { Chip } from "../design/Badge";
import { FEDER, UEBERBLENDUNG } from "../design/federn";
import { Briefgeist, BriefgeistAktiv } from "../design/Briefgeist";
import { Kachel } from "../design/Kachel";
import { kategorieSymbol } from "../design/kategorieSymbole";
import { Knopf } from "../design/Knopf";
import { Fehlertext } from "../design/Leer";
import { datum, datumGesprochen, datumsZeile } from "../format";
import { fokusAbgeben, fokusUebernehmen, useAbgelegtMelden, useErklaeren } from "./ablegen";
import { Satz } from "../welt/Blase";
import { fristTag } from "../welt/datum";
import { fristZustand, heuteBerlin } from "../welt/lage";
import { Geist } from "../welt/Geist";
import { leben } from "../welt/darf";
import { bewegungReduziert } from "../welt/bewegung";
import { useWelt } from "../welt/einstellungen";
import { useGeistOptional } from "../welt/geistKontext";
import { Zustand } from "../welt/Zustand";
import type { SatzTeil } from "../welt/blasenForm";
import { PruefenSheet } from "./PruefenSheet";
import { SPERRE_MS, VorschlagGeaendertHinweis } from "./VorschlagGeaendertHinweis";
import { ausgang, briefAblegen } from "../buehne/ablageflug";
import { briefAus } from "../buehne/papierbrief";
import { abgeleiteteGruende, aenderungen, kartenZitat, kopfDatum, ratenplanAngebot, ratenplanFrage, ratenplanJa, vorschlagAblage, type Uebernommen } from "./vorschlag";

/** So lange steht „Abgelegt in …“ auf der Karte, bevor die naechste nachrueckt (Plan 6.1). Lesezeit,
 *  keine Bewegung: gilt auch bei reduzierter Bewegung. */
export const ERFOLG_MS = 1400;

/** W47i (WI1, WI2): der Vorschlag, den die Karte zeigt und mit „Passt“ sendet, mit der Kennung aus derselben Antwort.
 *  `kennung` undefined: das Detail nennt keine (Server vor W47i), dann geht kein Feld mit. `nr`: der gemeinsame Stand
 *  (`useGezeigt`), der beim Anheften schon da war; nur spaetere uebernimmt die Karte. */
type Angeheftet = { fuer: string; vorschlag: Vorschlag | null; kennung: string | undefined; nr: number };

function anheften(d: DokumentDetail, nr: number): Angeheftet {
  return { fuer: d.id, vorschlag: d.vorschlag, kennung: "vorschlag_id" in d ? d.vorschlag_id : undefined, nr };
}

/** Der Satz in Teilen (W37b, Verifier R1 H3): jedes Datum bleibt ganz (`nowrap`), auch wenn der Satz
 *  bei 320 px und 200 % sonst mitten im Wort bricht (`wrap-anywhere`), etwa „12.01.|2026“; das Komma
 *  danach haengt am Datum, damit keine Zeile mit „,“ beginnt. */
function SatzMitDaten(props: { satz: string }) {
  return props.satz.split(/(\d{2}\.\d{2}\.\d{4}[,.]?)/).map((teil, i) =>
    i % 2 ? (
      <span key={i} className="whitespace-nowrap" data-testid="vorschlag.satz.datum">
        {teil}
      </span>
    ) : (
      teil
    ),
  );
}

/** W82-2 N3 (R11 N1 `.beleg mark`): die vorgeschlagene Frist („12.11.2026“) im Zitat hervorgehoben, nur als Bild (ein `span`,
 *  kein `mark`: der Vorlesebaum bleibt); der Text bleibt gleich. */
function markiert(zitat: string, fristIso: string | undefined) {
  const tag = fristIso ? datum(fristIso) : "";
  const i = tag ? zitat.indexOf(tag) : -1;
  if (i < 0) return zitat;
  return (
    <>
      {zitat.slice(0, i)}
      <span className="v-mark">{tag}</span>
      {zitat.slice(i + tag.length)}
    </>
  );
}

/** „✓ übernommen“ hinter dem Briefdatum und der Kategorie (D-079, W62), in beiden Zeilen gleich. W64: bei 320 px und
 *  200 % ist es breiter als die Zeile; es bricht dann im Wort („über-nommen“, `hyphens-auto` wie W37c H5), statt in den
 *  Innenabstand der Karte zu reichen. Getrennt wird nur „übernommen“: auf der ganzen Zeile trennte Chromium auch
 *  „Versiche-rung“, das ganz in die Zeile passt. Haken und Wort bleiben ein Block (inline-flex): rein im Textfluss bricht
 *  Chromium zwischen Symbol und Wort, der Haken stuende allein am Zeilenende. */
function Uebernommen() {
  return (
    <span className="inline-flex max-w-full items-start gap-1 text-pg-ok">
      {/* mittig in der ersten Textzeile (1lh), nicht zwischen „über-“ und „nommen“ */}
      <span className="flex h-lh shrink-0 items-center">
        <Check size={16} strokeWidth={2} aria-hidden />
      </span>
      <span className="min-w-0 hyphens-auto">übernommen</span>
    </span>
  );
}

/** Vorschlagskarte „Bitte pruefen“ (Handoff PgInbox, Plan 6.1): Kopf mit Absender und Betreff (fuehrt
 *  zum Brief), der Vorschlag als Satz mit der Briefgeist-Glyph, ein Beleg aus dem Brief, dann „Passt“
 *  (legt den Vorschlag unveraendert ab), „Anders“ (oeffnet das Formular) und „Brief erklaeren“.
 *  Vor den Knoepfen der Unsicher-Chip mit Lernhinweis, wenn die Kategorie unsicher war. Ohne
 *  vorgeschlagene Kategorie bleibt „Anders“ der einzige Weg. Ein unsicheres Briefdatum steht als eigene
 *  Zeile mit „Übernehmen“ da (D-079); uebernommen geht es mit „Passt“ mit und ist in „Anders“
 *  vorbelegt. Einen erkannten Ratenplan zeigt die Karte nicht; „Passt“ fragt dann erst, ob er in die Fixkosten soll (W38-5,
 *  F44b B). Nach „Passt“ zeigt die Karte kurz den
 *  Erfolg (gruen umrandet, Haken, „Abgelegt in <Kategorie>“, aria-live), erst dann laedt die Liste neu
 *  und die naechste Karte rueckt nach; der Toast „Abgelegt.“ entfaellt dann (eine Meldung reicht).
 *  Dieselbe Karte steht im Posteingang, als Stapel in Kennenlernen (F14) und oben in der Dokumentansicht
 *  (W37i-Web); dort hat die Ansicht das Detail gerade geholt (`geladen`), die Karte ruft es nicht noch einmal ab.
 *  In der Ansicht fuehrte der Kopf auf den Brief selbst: mit `kopfOhneLink` ist er reiner Text ohne Chevron (W37i-2).
 *  `onAblegen` meldet, ob „Passt“ gerade ablegt oder den Erfolg zeigt; die Ansicht sperrt dann ihren Weg ueber das
 *  Blatt (W37i-2, Verifier R1 H3). */
export function Vorschlagskarte(props: {
  dokument: Dokument;
  mehrerePersonen: boolean;
  geladen?: boolean;
  kopfOhneLink?: boolean;
  onAblegen?: (aktiv: boolean) => void;
  /** W82: Stimme über dem Brief (Funke-Zeichen und `stimmeVorschlag`), nur auf der Startseite */
  stimme?: SatzTeil[];
  /** W82: nach „Passt“ abgelegt (Antwort des Geists); `kategorie` ist der Name aus „Abgelegt in …“, `fristen` sind die mit
   *  abgelegten Fristen (sie ändern die Lage, Stack-R2 F1) */
  onAbgelegt?: (karte: HTMLElement | null, kategorie: string, fristen: { datum: string }[]) => void;
  /** W82 Teil 3 (Dokumentansicht, F91): die Figur an der Sprechzeile ist der Platz des Geists der Seite (Sicht-Regel) */
  geistPlatz?: boolean;
  /** W82 Web-5h3 (HW13): der Kopf ist still (Zeichen statt Figur), etwa „Nur bei Erklärungen“ auf der Startseite */
  stimmeStill?: boolean;
  /** W82 Web-5h3 (HW9): nach „Passt“ hält die Karte ihre Höhe, bis sie geht (kein Zusammenklappen unter dem Gaist); sie ist
   *  dann der Bezug seines Mitgleitens (`data-geist-bezug`) */
  flaecheHalten?: boolean;
  /** W81 (S4): an das Pruefen-Blatt von „Anders“ weitergereicht (nur die Dokumentansicht, F4 A) */
  onKorrigiert?: () => void;
  /** W82 Web-Antwort-Ablegen (D-796, A1): außerhalb der Karte abgelegt (Blatt „Prüfen“ der Dokumentansicht), solange sie für die
   *  Antwort steht: derselbe Erfolg wie nach „Passt“ („Abgelegt in …“), mit `flaecheHalten` in der Höhe davor (`hoehe`) */
  abgelegtIn?: { name: string; hoehe: number };
}) {
  const { data: detail } = useDokument(props.dokument.id, !props.geladen);
  const namen = useNamen();
  const { data: akten } = useAkten();
  const { data: personen } = usePersonen();
  // W47i (WI2): angeheftet ist der erste Vorschlag, den die Karte zeigt; neue Daten aus dem Cache (Fensterfokus,
  // Neuladen nach einer anderen Ablage) ersetzen ihn nicht still. Neu angeheftet wird nur, wenn die Karte noch keinen
  // Vorschlag gezeigt hat (Brief kam eben aus der Verarbeitung) oder fuer einen anderen Brief steht; nach einem 409 gilt
  // der gemeinsame Stand (WI6), auch wenn der 409 aus dem Blatt der Dokumentansicht kam
  const geteilt = useGezeigt(props.dokument.id);
  const [angeheftet, setAngeheftet] = useState<Angeheftet | null>(null);
  if (detail && detail.id === props.dokument.id && (!angeheftet || angeheftet.fuer !== detail.id || (angeheftet.vorschlag === null && detail.vorschlag !== null))) {
    setAngeheftet(anheften(detail, geteilt?.nr ?? 0));
  }
  const gueltig = angeheftet?.fuer === props.dokument.id ? angeheftet : null;
  // nach dem Anheften gekommen: der 409 dieses Briefs mit neuem Vorschlag, Kennung und Zeilen
  const neu = gueltig && geteilt && geteilt.nr > gueltig.nr ? geteilt : undefined;
  const gezeigt = neu ? { vorschlag: neu.vorschlag, kennung: neu.vorschlag_id } : gueltig;
  const bestaetigen = useBestaetigen(props.dokument.id, ERFOLG_MS, gezeigt?.vorschlag);
  // WI7: nach dem 409 kurz gesperrt, gegen den Doppeltipp
  const [gesperrt, setGesperrt] = useState(false);
  const sperrUhr = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(sperrUhr.current), []);
  const hinweis = useRef<HTMLDivElement>(null);
  const fokusZumHinweis = useRef(false);
  useEffect(() => {
    if (!neu || !fokusZumHinweis.current) return;
    fokusZumHinweis.current = false;
    hinweis.current?.focus();
  }, [neu]);
  const ablegenAktiv = bestaetigen.isPending || bestaetigen.isSuccess;
  const [weltStufe] = useWelt();
  const geistApi = useGeistOptional();
  const figurPlatz = useRef<HTMLSpanElement>(null);
  const { geistPlatz } = props;
  const platzMelden = geistApi?.platzMelden;
  // F91 (D-456): in der Dokumentansicht ist die Figur an der Sprechzeile sein Platz; geht die Karte, hat er keinen mehr
  useEffect(() => {
    if (!geistPlatz || !platzMelden) return;
    platzMelden(figurPlatz.current);
    return () => platzMelden(null);
  }, [geistPlatz, platzMelden]);
  // W82-2 N3: auf der Startseite (mit Stimme) traegt die Karte die Gestalt der Vorlage R11 N1 „Bitte prüfen“
  const welt = Boolean(props.stimme);
  const { onAblegen } = props;
  useEffect(() => {
    onAblegen?.(ablegenAktiv);
    return () => onAblegen?.(false);
  }, [ablegenAktiv, onAblegen]);
  const abgelegtMelden = useAbgelegtMelden();
  const { erklaeren, laeuft } = useErklaeren();
  const [anders, setAnders] = useState(false);
  const [abgelegt, setAbgelegt] = useState<string | null>(null);
  const [festHoehe, setFestHoehe] = useState<number | null>(null);
  // D-796 (A1): der Erfolg nach „Passt“ oder von außen nach dem Blatt
  const erfolgIn = abgelegt ?? props.abgelegtIn?.name ?? null;
  const hoeheFest = abgelegt !== null ? festHoehe : props.flaecheHalten ? (props.abgelegtIn?.hoehe ?? null) : null;
  // D-079: in der Karte bewusst uebernommene unsichere Werte (Briefdatum, seit W62 die Kategorie). W47i (WI3): sie gehoeren zum
  // Vorschlag, fuer den sie uebernommen wurden; nach einem 409 gilt keiner mehr (`nr`)
  const [uebernahme, setUebernahme] = useState<{ nr: number; werte: Uebernommen }>({ nr: 0, werte: {} });
  const stand = neu?.nr ?? 0;
  const uebernommen = uebernahme.nr === stand ? uebernahme.werte : {};
  const passt = useRef<HTMLButtonElement>(null);
  // W38-5 (F44b B, `plan-w38-5b.md` 5.2): bei einem erkannten Plan fragt „Passt“ erst nach; die Frage gehoert wie die
  // Uebernahme zum gezeigten Vorschlag (`nr`), nach einem 409 gilt sie nicht mehr. `sendet`: einmal gesendet ist gesendet,
  // bis die Antwort da ist (kein zweiter Aufruf, auch bei zwei Tipps im selben Bildtakt)
  const [frage, setFrage] = useState<number | null>(null);
  const sendet = useRef(false);
  const nein = useRef<HTMLButtonElement>(null);
  // Verifier R1 A1 (Doppeltipp): die Frage steht sofort, und bei 320 px und 200 % liegt „Ja“ oder „Nein“ genau unter
  // „Passt“. Ein Tipp (Maus, Finger) kurz nach dem Oeffnen zaehlt darum nicht, wie „Passt“ nach dem 409 (WI7); die
  // Tastatur steht auf „Nein“ und zaehlt sofort (`detail` 0)
  const [frageBereit, setFrageBereit] = useState(false);
  const bereitUhr = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(bereitUhr.current), []);
  const ruhig = useReducedMotion();
  const karte = useRef<HTMLLIElement>(null);
  const erfolg = useRef<HTMLParagraphElement>(null);
  // Lag der Fokus beim Tipp auf „Passt“ in der Karte, wandert er zur Meldung (der Knopf verschwindet)
  const fokusMit = useRef(false);
  useEffect(() => {
    if (abgelegt !== null && fokusMit.current) erfolg.current?.focus();
  }, [abgelegt]);
  // F2: rueckt die naechste nach, waehrend der Fokus noch in der erledigten Karte liegt, geht er an
  // deren Kopf-Link. Aufraeumen im Layout-Effekt: dann haengt die Karte noch im Dokument.
  const kopf = useRef<HTMLAnchorElement>(null);
  useLayoutEffect(() => {
    const k = karte.current;
    return () => {
      if (fokusMit.current && k?.hasAttribute("data-abgelegt") && k.contains(document.activeElement)) fokusAbgeben();
    };
  }, []);
  useEffect(() => fokusUebernehmen(kopf.current), []);
  // Andere Felder des Briefs (Datum, Person, Status) kommen weiter aus dem frischen Detail, nur der Vorschlag ist angeheftet
  const d = detail ?? props.dokument;
  const v = detail ? (gezeigt ?? anheften(detail, 0)).vorschlag : undefined;
  const ablage = vorschlagAblage(
    v,
    {
      kategorie: (id) => namen.kategorie(id)?.name,
      person: (id) => personen?.find((p) => p.id === id)?.name,
      akte: (id) => akten?.find((a) => a.id === id)?.name,
      akteStatus: (id) => akten?.find((a) => a.id === id)?.status,
    },
    props.mehrerePersonen,
    uebernommen,
    // W37g (H2): ein bestaetigtes Datum gilt in Satz, Kopf und Body, der Vorschlag ueberschreibt es nicht
    d.datum,
    // W47i (WI1): die Kennung aus derselben Antwort wie der Vorschlag, nur wenn der Server sie nennt
    gezeigt?.kennung,
  );
  const namenKurz = {
    kategorie: (id: string) => namen.kategorie(id)?.name,
    person: (id: string) => personen?.find((p) => p.id === id)?.name,
    akte: (id: string) => akten?.find((a) => a.id === id)?.name,
  };
  // W37g-Web (Verifier W37f R1 H2): ein Absender nur aus Leerraum ist kein Vorschlag, wie in Kopf und Zeile
  const vorgeschlagen = v?.felder?.absender?.wert;
  const absender = namen.absender(d.absender_id) ?? (vorgeschlagen?.trim() ? vorgeschlagen : "Neuer Brief");
  // W37i-2 (Verifier R1 H6): jedes Feld des Vorschlags kann fehlen, wie in vorschlagAblage
  const betreff = d.betreff ?? v?.felder?.betreff?.wert;
  // D-079: ein uebernommenes Datum macht aus „Eingang …“ sofort „Brief vom …“ (Anzeige und Screenreader).
  // W37e (Sichtpruefung 08.10.): ein sicheres auch, solange der Brief nicht bestaetigt ist; der Server setzt
  // `datum` erst beim Bestaetigen. Dieselbe Regel wie der Satz (kopfDatum, vorschlagDatum)
  const datumVorschlag = ablage?.datumVorschlag;
  const kategorieVorschlag = ablage?.kategorieVorschlag;
  const mitDatum = { ...d, datum: kopfDatum(d, v, uebernommen) };
  const eingang = datumsZeile(mitDatum);
  // W73 (D-278): der Auszug in ganzen Saetzen, ohne ihn der Beleg
  const beleg = kartenZitat(v);
  // W82 Web-8 W8-3 (W88 NW-4): der Grund einer abgeleiteten Zahlfrist, die „Passt“ mitschickt (Text des Servers)
  const abgeleitet = abgeleiteteGruende(v);
  const lernen = props.mehrerePersonen ? "ihr sortiert" : "du sortierst";
  // W38-5: der Plan geht nie still mit (rateSicher), nur nach „Ja“; ohne „Passt“ (keine Ablage) gibt es keine Frage
  const plan = ablage ? ratenplanAngebot(v) : null;
  const frageOffen = plan !== null && frage === stand && abgelegt === null;

  function ablegen(a: NonNullable<typeof ablage>, koerper: Bestaetigung) {
    if (sendet.current) return;
    sendet.current = true;
    bestaetigen.mutate(koerper, {
      onSuccess: (d) => {
        setFrage(null);
        const ziel = namen.kategorie(a.bestaetigung.kategorie_id ?? null)?.name ?? "der Ablage";
        // HW9: die Höhe vor dem Erfolg, gemessen, bevor der Vorschlag verschwindet
        if (props.flaecheHalten && karte.current) setFestHoehe(karte.current.getBoundingClientRect().height);
        // W82 Design-Web-1 (Teil B a): der Brief fliegt als Papier in die Ablage (Reiter Akten bzw. Archiv), gemessen vor dem Erfolg
        briefAblegen(ausgang(karte.current), briefAus(absender, "pruefen", 0));
        setAbgelegt(ziel);
        abgelegtMelden(d, true);
        props.onAbgelegt?.(karte.current, ziel, koerper.fristen ?? []);
      },
      // W47i (WI3, WI7): den neuen Stand hat `useBestaetigen` schon geschrieben; hier nur Sperre und Fokus
      onError: (fehler) => {
        sendet.current = false;
        setFrage(null);
        if (!vorschlagGeaendert(fehler)) return;
        setGesperrt(true);
        clearTimeout(sperrUhr.current);
        sperrUhr.current = setTimeout(() => setGesperrt(false), SPERRE_MS);
        // der Hinweis kann schon stehen (zweiter 409, oder der neue Stand kam vor diesem Rueckruf an)
        fokusZumHinweis.current = fokusMit.current;
        if (fokusMit.current) hinweis.current?.focus();
      },
    });
  }
  function antworten(e: MouseEvent, a: NonNullable<typeof ablage>, koerper: Bestaetigung) {
    if (e.detail > 0 && !frageBereit) return;
    ablegen(a, koerper);
  }
  // W82-2 N3 (R11 N1 `.v-brief`): Linse mit Zustandspunkt, Titel in Bricolage, rechts die vorgeschlagene Frist gross
  const kopfFrist = welt ? ablage?.bestaetigung.fristen?.find((fr) => /^\d{4}-\d{2}-\d{2}$/.test(fr.datum))?.datum : undefined;
  // W82 Teil 3 (Vormerkung R2): der Zustandspunkt an der Linse nach der Fälligkeit der vorgeschlagenen Frist (überfällig Raute,
  // bald Punkt, später Ring), ohne Frist der Ring
  const kopfZustand = kopfFrist ? fristZustand({ datum: kopfFrist, erledigt: null }, heuteBerlin()) : "spaeter";
  const kopfInhalt = (
    <>
      {welt ? (
        <span className="post-linse">
          <Kachel schluessel={namen.kategorie(d.kategorie_id)?.schluessel} />
          <span className="zp" data-zustand={kopfZustand} aria-hidden="true" />
        </span>
      ) : (
        <Kachel schluessel={namen.kategorie(d.kategorie_id)?.schluessel} />
      )}
      <span className="flex min-w-0 flex-1 flex-col">
        <span className={`t-body t-body-medium ${welt && props.kopfOhneLink ? "v-titel v-titel-ganz" : `truncate ${welt ? "v-titel" : ""}`}`}>{absender}</span>
        {/* D-073: gekuerzt wird nur der Betreff, das Datum bleibt ganz stehen (mobil fiel es sonst weg).
            W22d-w8b (Verifier w8 B1): der Betreff behaelt eine Mindestbreite (basis-20) und waechst bis zu
            seiner Laenge; reicht der Platz daneben nicht, rutscht das Datum als Ganzes in die naechste Zeile.
            Der Trenner haengt am Betreff, keine Zeile beginnt mit „·“. */}
        <span className="t-footnote flex min-w-0 flex-wrap gap-x-1 text-pg-text-2">
          {betreff && (
            <span className="flex min-w-0 max-w-max grow basis-20" data-testid="vorschlag.betreff">
              <span className="truncate">{betreff}</span>
              <span className="shrink-0">{"\u00a0·"}</span>
            </span>
          )}
          <span className="max-w-full" data-testid="vorschlag.datum">
            {eingang}
          </span>
        </span>
      </span>
    </>
  );

  return (
    <li
      ref={karte}
      data-abgelegt={erfolgIn !== null ? "" : undefined}
      data-geist-bezug={props.flaecheHalten ? props.dokument.id : undefined}
      style={hoeheFest !== null && erfolgIn !== null ? { minHeight: hoeheFest } : undefined}
      className={`vorschlag zeile relative list-none transition-shadow duration-(--pg-dur) ${erfolgIn !== null ? "rounded-group shadow-(--pg-erfolg-rand)" : ""}`}
    >
      {props.stimme && props.stimme.length > 0 && (
        <p className="vorschlag-stimme w-lesetext" data-testid="vorschlag.stimme" data-still={props.stimmeStill && weltStufe.praesenz !== "keine" ? "" : undefined}>
          {/* FW11 und Vormerkung R2: wo der Gaist spricht, sitzt seine Figur mit Licht (40) an der Sprechzeile; sachlich
              („Keine Erklärungen“) steht seine Glyphe */}
          {weltStufe.praesenz === "keine" || props.stimmeStill ? (
            <Briefgeist size={22} aria-hidden className="stimme-funke stimme-marke" />
          ) : (
            <span ref={figurPlatz} className="figur-stimme" aria-hidden="true">
              <Geist groesse={40} leben={{ ...leben(weltStufe.fassung, weltStufe.praesenz, bewegungReduziert()), teilchen: false, schweben: 0, neigung: 0 }} />
            </span>
          )}
          <span>
            <Satz teile={props.stimme} />
          </span>
        </p>
      )}
      {props.kopfOhneLink ? (
        <div className={welt ? "v-brief v-brief-ganz" : "contents"}>
          <div className="zeile-flex flex min-h-(--pg-hit) items-center gap-3 px-4 py-3" data-testid="vorschlag.kopf">
            {kopfInhalt}
          </div>
          {kopfFrist && (
            <span className="v-frist" aria-hidden="true">
              <b className="w-frist-vorschlag">{fristTag(kopfFrist, heuteBerlin())}</b>
              <span>Frist</span>
            </span>
          )}
        </div>
      ) : (
        <div className={welt ? "v-brief" : "contents"}>
          <Link
            ref={kopf}
            to={`/dokument/${d.id}`}
            className="zeile-flex flex min-h-(--pg-hit) min-w-0 flex-1 items-center gap-3 px-4 py-3 hover:bg-pg-surface-2"
            aria-label={`${absender}${betreff ? `, ${betreff}` : ""}, ${datumGesprochen(mitDatum)}`}
            data-testid="vorschlag.kopf"
          >
            {kopfInhalt}
            <ChevronRight size={16} strokeWidth={2} aria-hidden className="shrink-0 text-pg-text-3" />
          </Link>
          {/* die Frist steht im Satz und im Lesetext von „Passt“; hier nur gross als Bild (R11 N1 `.v-frist`) */}
          {kopfFrist && (
            <span className="v-frist" aria-hidden="true">
              <b className="w-frist-vorschlag">{fristTag(kopfFrist, heuteBerlin())}</b>
              <span>Frist</span>
            </span>
          )}
        </div>
      )}
      {/* Live-Region steht von Anfang an, sonst liest der Screenreader die Meldung nicht sicher vor */}
      <div aria-live="polite">
        {erfolgIn !== null && (
          <motion.p
            ref={erfolg}
            tabIndex={-1}
            initial={ruhig ? { opacity: 0 } : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={ruhig ? UEBERBLENDUNG : FEDER.sheetAuf}
            // R1 W82 Web-Antwort-Ablegen: bei 320 px und 200 % ist „Forderungen“ breiter als der Platz neben dem Haken; getrennt statt über den
            // Rand der Karte (wie „übernommen“, W64). Am Absatz, nicht an einem eigenen span: `.erfolg > span` ist der Grund des Hakens (welt.css)
            className="erfolg linie-oben t-body flex items-center gap-3 px-4 py-4 font-semibold text-pg-ok outline-none hyphens-auto wrap-anywhere"
          >
            <span className="grid size-(--size-tile) shrink-0 place-items-center rounded-pill bg-pg-ok-soft">
              <Check size={20} strokeWidth={2} aria-hidden />
            </span>
            Abgelegt in {erfolgIn}
          </motion.p>
        )}
      </div>
      <div className="linie-oben min-w-0 px-4 pt-3 pb-4" hidden={erfolgIn !== null}>
        {/* W47i (WI4): ueber dem Satz, bis zum naechsten Erfolg oder bis die Karte geht */}
        {neu && <VorschlagGeaendertHinweis ref={hinweis} band className="-mx-4 -mt-3 mb-3" detail={neu.detail} zeilen={aenderungen(neu.vorher, neu.vorschlag, neu.geaendert, namenKurz)} />}
        {ablage ? (
          <>
            {/* W82-2 N3: auf der Startseite ist der Satz der Lesetext (unsichtbar, an seinem Platz gesetzt), die Fakten darunter
                sind das Bild (R11 N1: Satz fuer Vorlesen, Marken `aria-hidden`) */}
            <p className={`t-body flex items-start gap-2 wrap-anywhere ${welt ? "v-satz-lesetext" : ""}`}>
              <Briefgeist size={18} aria-hidden className="mt-0.5 shrink-0 text-pg-ai" />
              <span className="min-w-0">
                <span className="sr-only">Vorschlag: </span>
                <SatzMitDaten satz={ablage.satz} />
              </span>
            </p>
            {welt && (
              <ul className="v-fakten" aria-hidden="true" data-testid="vorschlag.fakten">
                {ablage.fakten.map((f) => {
                  const Symbol = f.art === "brief" ? FileText : f.art === "akte" ? Archive : kategorieSymbol(namen.kategorie(ablage.bestaetigung.kategorie_id ?? null)?.schluessel);
                  return (
                    <li key={`${f.art}-${f.text}`} className="fakt" data-art={f.art} data-text={f.text}>
                      {f.art === "frist" ? <Zustand form={kopfZustand === "ueberfaellig" ? "raute" : kopfZustand === "bald" ? "punkt" : "ring"} /> : <Symbol size={15} strokeWidth={2} aria-hidden />}
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        ) : (
          <p className="t-body text-pg-text-2">{detail ? "Für diesen Brief gibt es keinen Vorschlag. Ordne ihn selbst ein." : "Vorschlag wird geladen …"}</p>
        )}
        {beleg &&
          (welt ? (
            // W82-2 N3 (R11 N1 `.beleg`): der Beleg mit Kopf „Aus dem Brief“ und dem Funken (nur Bild, das Zitat traegt die
            // Anfuehrung), Linie darueber, die vorgeschlagene Frist im Zitat hervorgehoben; an der Stelle des Bestands, damit
            // die Reihenfolge beim Vorlesen bleibt
            <div className="v-beleg">
              <p className="v-beleg-kopf" aria-hidden="true">
                <Sparkle className="v-funke" size={13} strokeWidth={2.5} aria-hidden />
                Aus dem Brief
              </p>
              <p className="t-footnote mt-2 text-pg-text-2 italic wrap-anywhere">„{markiert(beleg, kopfFrist)}“</p>
            </div>
          ) : (
            <p className="t-footnote mt-2 text-pg-text-2 italic wrap-anywhere">„{beleg}“</p>
          ))}
        {ablage &&
          abgeleitet.map((g) => (
            <p key={g} className="t-footnote mt-2 break-words hyphens-auto text-pg-text-2" data-testid="vorschlag.abgeleitet">
              {g}
            </p>
          ))}
        {/* D-079: unsicheres Briefdatum als eigene Zeile vor den Knoepfen; „Übernehmen“ nimmt es in die
            Ablage, dann nennt der Satz es und „Passt“ schickt es mit. Ohne Uebernehmen geht es nicht mit (P4). */}
        {datumVorschlag && (
          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1">
            {datumVorschlag.uebernommen ? (
              // Textfluss statt Flex: bei grosser Schrift bricht die Zeile an Leerzeichen, das Datum bleibt ganz (320 px bei
              // 200 % passt „Briefdatum 12.01.2026“ nicht in eine Zeile); „übernommen“ wird dort getrennt (W64, Uebernommen)
              <p className="t-subheadline min-w-0 wrap-anywhere" data-testid="vorschlag.datum.uebernommen">
                Briefdatum <span className="t-zahl whitespace-nowrap">{datum(datumVorschlag.wert)}</span> <Uebernommen />
              </p>
            ) : (
              <>
                <p className="t-subheadline min-w-0">
                  Briefdatum <span className="t-zahl whitespace-nowrap">{datum(datumVorschlag.wert)}</span>
                  <span className="text-pg-text-2">, {datumVorschlag.sicherheit}</span>
                </p>
                <Knopf
                  art="sekundaer"
                  // W37c (Verifier R1 H5): bei 320 px und 200 % ist „Übernehmen“ breiter als der Platz neben
                  // dem Innenabstand; getrennt („Über-nehmen“) bleibt der Abstand, statt dass der Text ihn fuellt
                  className="hyphens-auto"
                  data-testid="vorschlag.datum.uebernehmen"
                  aria-label={`Briefdatum ${datum(datumVorschlag.wert)} übernehmen`}
                  disabled={bestaetigen.isPending || bestaetigen.isSuccess}
                  onClick={() => {
                    setUebernahme({ nr: stand, werte: { ...uebernommen, datum: true } });
                    // Der Knopf verschwindet: der Fokus geht an „Passt“, den naechsten Schritt
                    requestAnimationFrame(() => passt.current?.focus());
                  }}
                >
                  Übernehmen
                </Knopf>
              </>
            )}
          </div>
        )}
        {/* W62 (D-196): unsichere Kategorie als eigene Zeile wie das Datum (D-079), unter dessen Zeile; „Übernehmen“ nimmt den
            Chip „Kategorie unsicher“ weg. „Passt“ schickt die Kategorie mit und ohne Uebernehmen (W22b-3), der Satz nennt sie */}
        {kategorieVorschlag && (
          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1">
            {kategorieVorschlag.uebernommen ? (
              <p className="t-subheadline min-w-0 wrap-anywhere" data-testid="vorschlag.kategorie.uebernommen">
                Kategorie {kategorieVorschlag.name} <Uebernommen />
              </p>
            ) : (
              <>
                <p className="t-subheadline min-w-0 wrap-anywhere">
                  Kategorie {kategorieVorschlag.name}
                  <span className="text-pg-text-2">, {kategorieVorschlag.sicherheit}</span>
                </p>
                <Knopf
                  art="sekundaer"
                  // wie beim Datum (W37c H5): bei 320 px und 200 % getrennt statt bis an den Rand gefuellt
                  className="hyphens-auto"
                  data-testid="vorschlag.kategorie.uebernehmen"
                  aria-label={`Kategorie ${kategorieVorschlag.name} übernehmen`}
                  disabled={bestaetigen.isPending || bestaetigen.isSuccess}
                  onClick={() => {
                    setUebernahme({ nr: stand, werte: { ...uebernommen, kategorie: true } });
                    // Der Knopf verschwindet: der Fokus geht an „Passt“, den naechsten Schritt
                    requestAnimationFrame(() => passt.current?.focus());
                  }}
                >
                  Übernehmen
                </Knopf>
              </>
            )}
          </div>
        )}
        {/* Beschriftung bleibt „Passt“ / „Anders“ (entschieden vom Architekten, offene Fragen Punkt 9; nicht
            „Passt, ablegen“ / „Anders ablegen“ aus D-061 und Plan F7). Der Screenreader hoert dazu den
            Satz bzw. den Absender im aria-label. */}
        {/* W22b-3: der Unsicher-Hinweis als Chip vor den Knoepfen (Handoff PgInbox); er nennt nur, was
            „Passt“ trotz Unsicherheit mitschickt und der Satz zeigt, also die Kategorie. W62: nach „Übernehmen“ geht der
            Chip, der Lernhinweis bleibt (gelernt wird erst beim „Passt“, W12b). */}
        {ablage && (ablage.unsicher.length > 0 || kategorieVorschlag) && (
          <p className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1">
            {ablage.unsicher.length > 0 && (
              // W64: bei 320 px und 200 % breiter als die Karte; der Chip bricht um, der Lernhinweis steht darunter
              <Chip zustand="bald" icon={TriangleAlert} umbrechen>
                {ablage.unsicher.join(", ")} unsicher
              </Chip>
            )}
            <span className="t-footnote text-pg-text-2">Mit „Passt“ lernt Papiergaist, wie {lernen}.</span>
          </p>
        )}
        <div className={`mt-3 flex flex-wrap gap-2 ${welt ? "v-knoepfe-reihe" : ""}`}>
          {ablage && (
            <Knopf
              ref={passt}
              art={welt ? "passt" : "primaer"}
              icon={welt ? Check : undefined}
              disabled={bestaetigen.isPending || bestaetigen.isSuccess || gesperrt}
              onClick={() => {
                fokusMit.current = karte.current?.contains(document.activeElement) ?? false;
                if (plan) {
                  setFrage(stand);
                  setFrageBereit(false);
                  clearTimeout(bereitUhr.current);
                  bereitUhr.current = setTimeout(() => setFrageBereit(true), SPERRE_MS);
                } else ablegen(ablage, ablage.bestaetigung);
              }}
              aria-label={`Passt: ${ablage.satz}`}
            >
              Passt
            </Knopf>
          )}
          <Knopf art="sekundaer" icon={welt ? Pencil : undefined} onClick={() => setAnders(true)} disabled={!detail} aria-label={`Anders ablegen: ${absender}`}>
            Anders
          </Knopf>
          <Knopf art="text" icon={laeuft ? BriefgeistAktiv : Briefgeist} disabled={laeuft} onClick={() => erklaeren(d.id)} className={welt ? "v-erklaeren" : ""}>
            Brief erklären
          </Knopf>
        </div>
        {/* ein 409 VORSCHLAG_GEAENDERT ist kein Fehler, er steht als Hinweis ueber dem Satz (WI3, WI8) */}
        {bestaetigen.error && !vorschlagGeaendert(bestaetigen.error) && <Fehlertext fehler={bestaetigen.error} />}
      </div>
      {/* W38-5 (F44b B, `plan-w38-5b.md` 5.2): Hinweisdialog mit der Frage; „Ja“ legt mit der Rate des gezeigten Vorschlags ab,
          „Nein“ ohne Schluessel `rate`. Wegwischen (Escape, daneben) legt nichts ab, die Karte bleibt. Fokus auf „Nein“, der
          Wahl ohne Geld; waehrend des Sicherns sind beide gesperrt (aria-disabled, der Fokus bleibt) und nichts schliesst */}
      {ablage && plan && (
        <Dialog.Root open={frageOffen} onOpenChange={(auf) => !auf && !sendet.current && setFrage(null)}>
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 z-40 bg-pg-overlay" />
            <Dialog.Content
              role="alertdialog"
              aria-describedby={undefined}
              data-testid="vorschlag.ratenplan.frage"
              data-bereit={frageBereit || undefined}
              className="fixed top-1/2 left-1/2 z-50 flex max-h-(--sheet-max-h) w-(--w-dialog) max-w-(--dialog-max-w) -translate-x-1/2 -translate-y-1/2 flex-col gap-4 overflow-y-auto rounded-(--r-dialog) bg-pg-bg p-4 shadow-sheet"
              onOpenAutoFocus={(e) => {
                e.preventDefault();
                nein.current?.focus();
              }}
              onEscapeKeyDown={(e) => sendet.current && e.preventDefault()}
              // Verifier R2 (Tastatur): ein gehaltenes oder gleich wiederholtes Enter (Leertaste) nach dem Oeffnen trifft
              // „Nein“ nicht; erst nach der Sperre zaehlt die Taste wie der Tipp
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.repeat || !frageBereit) && e.preventDefault()}
              onInteractOutside={(e) => sendet.current && e.preventDefault()}
              onCloseAutoFocus={(e) => {
                // nach dem Ablegen nimmt die Meldung den Fokus, nach einem 409 der Hinweis; sonst zurueck auf „Passt“
                e.preventDefault();
                if (!karte.current?.contains(document.activeElement)) passt.current?.focus();
              }}
            >
              {/* bei 320 px und 200 % ist „übernehmen?“ breiter als der Dialog: getrennt statt abgeschnitten (wie W37c H5) */}
              <Dialog.Title className="t-headline break-words hyphens-auto">{ratenplanFrage(plan)}</Dialog.Title>
              <div className="flex flex-wrap justify-end gap-2">
                <Knopf
                  ref={nein}
                  art="sekundaer"
                  aria-disabled={bestaetigen.isPending || undefined}
                  onClick={(e) => antworten(e, ablage, ablage.bestaetigung)}
                >
                  Nein
                </Knopf>
                <Knopf
                  aria-label={ratenplanJa(plan)}
                  aria-disabled={bestaetigen.isPending || undefined}
                  onClick={(e) => antworten(e, ablage, { ...ablage.bestaetigung, rate: plan })}
                >
                  Ja
                </Knopf>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      )}
      {detail && (
        <PruefenSheet
          // W47i: das Blatt beginnt mit dem Vorschlag, den die Karte zeigt, samt dessen Kennung
          dokument={gezeigt ? { ...detail, vorschlag: gezeigt.vorschlag, ...(gezeigt.kennung === undefined ? {} : { vorschlag_id: gezeigt.kennung }) } : detail}
          offen={anders}
          onSchliessen={() => setAnders(false)}
          vorbelegung={datumVorschlag?.uebernommen ? { datum: datumVorschlag.wert } : undefined}
          onKorrigiert={props.onKorrigiert}
        />
      )}
    </li>
  );
}
